import { useCallback, useEffect, useRef } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Product,
	Severity,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import {
	ADD_PAYMENT_AVOID_DEACTIVATION_ACTION_SUBJECT_ID,
	AUNT_STATSIG_EXPERIMENT_KEY,
	REMOVE_USERS_AVOID_DEACTIVATION_ACTION_SUBJECT_ID,
	TAKE_ACTION_AVOID_DEACTIVATION_ACTION_SUBJECT_ID,
} from './constants';
import type { ErrorHandlerProps } from './types';

export const ANALYTICS_CHANNEL: FabricChannel.growth = FabricChannel.growth;

export function useAnalytics(): {
	createAddPaymentsButtonClickEvent: (source: string, attributes: Record<string, any>) => void;
	createRemoveUsersButtonClickEvent: (sourceId: string, attributes: Record<string, any>) => void;
	createTakeActionClickEvent: (sourceId: string, attributes: Record<string, any>) => void;
	growthErrorHandler: ({ error, errorInfo, product, source }: ErrorHandlerProps) => void;
	sendAnalyticEvent: (payload: Record<string, any>) => void;
} {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendAnalyticEvent = useCallback(
		(payload: Record<string, any>): void => {
			createAnalyticsEvent(payload).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const createAddPaymentsButtonClickEvent = (
		source: string,
		attributes: Record<string, any>,
	): void =>
		sendAnalyticEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: ADD_PAYMENT_AVOID_DEACTIVATION_ACTION_SUBJECT_ID,
			source,
			attributes,
		});

	const createTakeActionClickEvent = (sourceId: string, attributes: Record<string, any>): void =>
		sendAnalyticEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: TAKE_ACTION_AVOID_DEACTIVATION_ACTION_SUBJECT_ID,
			source: sourceId,
			attributes,
		});

	const createRemoveUsersButtonClickEvent = (
		sourceId: string,
		attributes: Record<string, any>,
	): void =>
		sendAnalyticEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: REMOVE_USERS_AVOID_DEACTIVATION_ACTION_SUBJECT_ID,
			source: sourceId,
			attributes,
		});

	const growthErrorHandler = ({ error, errorInfo, product, source }: ErrorHandlerProps): void =>
		sendAnalyticEvent(
			createProductSpecificAnalyticsIntegrationPayload({
				team: Team.GROWTH_LUNA,
				product: product === 'confluence' ? Product.CONFLUENCE : Product.JIRA,
				experimentMeta: {
					experimentKey: AUNT_STATSIG_EXPERIMENT_KEY,
				},
				errorMessage: error.message,
				errorType: ErrorType.GENERIC,
				source: source || AUNT_STATSIG_EXPERIMENT_KEY,
				severity: Severity.HIGH,
				additionalAttributes: {
					...errorInfo,
				},
			}),
		);

	return {
		sendAnalyticEvent,
		createAddPaymentsButtonClickEvent,
		createRemoveUsersButtonClickEvent,
		createTakeActionClickEvent,
		growthErrorHandler,
	};
}

export function ScreenEvent({
	name,
	attributes,
}: {
	attributes?: Record<string, any>;
	name: string;
}) {
	const { sendAnalyticEvent } = useAnalytics();
	const hasSentScreenEvent = useRef(false);

	useEffect(() => {
		if (hasSentScreenEvent.current) {
			return;
		}
		sendAnalyticEvent({
			eventType: 'screen',
			name,
			attributes,
		});
	}, [sendAnalyticEvent, name, attributes]);

	return null;
}
